// Module 09 §5 — the widget model.
//
// WIRE-PARITY RULE (ORP-18656, verified against production 2026-07-28): the persisted widget
// bag is the contract with the backend, and the backend READS INSIDE IT — `sections_validator`
// gates on `visibleType`, resolves table constraints from `selectedTable`, and 400s the save
// before any DB write. So the typed fields below are a convenience surface *over* the wire bag,
// never a replacement for it: `DashboardWidgetWire` keeps an index signature, and the
// serialiser re-emits the parsed object verbatim. A closed key list would silently drop the
// 17+ keys production carries that no contract ever inventoried (`defaultVisible` alone is on
// 47% of live widgets).

/** What a grid leaf renders. Built-in report kinds render via module 08; the rest via the registry. */
export type WidgetKind =
  // → module 08 report composites
  | 'chart'
  | 'table'
  | 'matrix'
  | 'card'
  | 'double'
  // → module 08 multi_metric overview (buildChartOptions)
  | 'dashboard_chart'
  // → dashboard widgets on 08's data layer
  | 'score'
  | 'list_table'
  // → feature-registered embeds (registry)
  | 'kpi'
  | 'funnel'
  | 'performance_overview'
  | 'alert'
  | 'anomalies'
  | 'recommendation'

/** Per-kind builder capability flags, ported from legacy `helpers.js` `VISIBLE_TYPES:183-314`. */
export interface WidgetCapabilities {
  kind: WidgetKind
  /** Legacy `subType` — differs from `kind` only for `card` (`media_card`). */
  subType: string
  /** i18n key: `dashboards.widgetKinds.<kind>` (replaces the inline English `header`). */
  labelKey: string
  /** Vendor-free icon token; module 02 maps it when the builder components land. */
  icon: string
  /** `recommendation` is commented out of the legacy catalog but still renders when persisted. */
  creatable: boolean
  needsLevel?: boolean
  sort?: boolean
  breakdown?: boolean
  datePicker?: boolean
  maxDim?: number
  maxMetric?: number
  /** May only sit at the top level of the grid. */
  onlyParent?: boolean
}

export const WIDGET_KIND_CATALOG: readonly WidgetCapabilities[] = [
  {
    kind: 'table',
    subType: 'table',
    labelKey: 'dashboards.widgetKinds.table',
    icon: 'table',
    creatable: true,
    needsLevel: true,
    sort: true,
    breakdown: true,
    datePicker: true,
  },
  {
    kind: 'matrix',
    subType: 'matrix',
    labelKey: 'dashboards.widgetKinds.matrix',
    icon: 'table-cells-large',
    creatable: true,
    needsLevel: true,
    datePicker: true,
    maxDim: 1,
    maxMetric: 1,
  },
  {
    kind: 'chart',
    subType: 'chart',
    labelKey: 'dashboards.widgetKinds.chart',
    icon: 'chart-waterfall',
    creatable: true,
    needsLevel: true,
    sort: true,
    datePicker: true,
  },
  {
    kind: 'double',
    subType: 'double',
    labelKey: 'dashboards.widgetKinds.double',
    icon: 'diagram-predecessor',
    creatable: true,
    needsLevel: true,
    datePicker: true,
  },
  {
    kind: 'performance_overview',
    subType: 'performance_overview',
    labelKey: 'dashboards.widgetKinds.performanceOverview',
    icon: 'table-list',
    creatable: true,
    onlyParent: true,
  },
  {
    // Commented out at `helpers.js:232-239` — not offered in the builder, but persisted
    // dashboards still render it (`CustomDashboardDataVisible.vue:108`).
    kind: 'recommendation',
    subType: 'recommendation',
    labelKey: 'dashboards.widgetKinds.recommendation',
    icon: 'magnifying-glass-chart',
    creatable: false,
    onlyParent: true,
  },
  {
    kind: 'alert',
    subType: 'alert',
    labelKey: 'dashboards.widgetKinds.alert',
    icon: 'bell',
    creatable: true,
    onlyParent: true,
  },
  {
    kind: 'anomalies',
    subType: 'anomalies',
    labelKey: 'dashboards.widgetKinds.anomalies',
    icon: 'chart-fft',
    creatable: true,
    onlyParent: true,
  },
  {
    kind: 'card',
    subType: 'media_card',
    labelKey: 'dashboards.widgetKinds.card',
    icon: 'image',
    creatable: true,
    needsLevel: true,
    sort: true,
    breakdown: true,
    onlyParent: true,
  },
  {
    kind: 'list_table',
    subType: 'list_table',
    labelKey: 'dashboards.widgetKinds.listTable',
    icon: 'line-columns',
    creatable: true,
    needsLevel: true,
    sort: true,
    datePicker: true,
    maxDim: 1,
    maxMetric: 1,
    onlyParent: true,
  },
  {
    kind: 'dashboard_chart',
    subType: 'dashboard_chart',
    labelKey: 'dashboards.widgetKinds.dashboardChart',
    icon: 'chart-line',
    creatable: true,
    needsLevel: true,
    datePicker: true,
    onlyParent: true,
  },
  {
    kind: 'funnel',
    subType: 'funnel',
    labelKey: 'dashboards.widgetKinds.funnel',
    icon: 'chart-pyramid',
    creatable: true,
    onlyParent: true,
  },
  {
    kind: 'score',
    subType: 'score',
    labelKey: 'dashboards.widgetKinds.score',
    icon: 'credit-card-blank',
    creatable: true,
    needsLevel: true,
    datePicker: true,
    onlyParent: true,
  },
  {
    kind: 'kpi',
    subType: 'kpi',
    labelKey: 'dashboards.widgetKinds.kpi',
    icon: 'bullseye-arrow',
    creatable: true,
    onlyParent: true,
  },
]

const CATALOG_BY_KIND = new Map<string, WidgetCapabilities>(
  WIDGET_KIND_CATALOG.map((entry) => [entry.kind, entry]),
)

export function widgetCapabilities(kind: WidgetKind): WidgetCapabilities | null {
  return CATALOG_BY_KIND.get(kind) ?? null
}

export function isWidgetKind(value: unknown): value is WidgetKind {
  return typeof value === 'string' && CATALOG_BY_KIND.has(value)
}

/** Per-widget date selection (per-widget date pickers are a dashboard feature). */
export interface WidgetDateRange {
  date_start?: string | null
  date_end?: string | null
  compare_date_start?: string | null
  compare_date_end?: string | null
}

/**
 * The persisted widget bag, as it sits on a `cols[]` entry.
 *
 * Every field is optional because production carries every combination, and the index
 * signature is load-bearing: it is what makes unknown-key preservation total rather than
 * limited to the keys someone remembered to list.
 */
export interface DashboardWidgetWire {
  /** The kind discriminator the backend gates on. Written back verbatim, never re-derived. */
  visibleType?: string
  id?: string
  name?: string
  // ── report / level reference ──
  reportId?: number
  partition?: string
  partition_only?: boolean
  accountWorkspaces?: (number | string)[]
  isAdLevel?: boolean
  // ── table config ──
  setDimensions?: string[]
  setMetrics?: string[]
  defaultBreakdown?: string | null
  sortField?: string | null
  rowCount?: number | null
  matrixDimension?: string | null
  previewMetric?: string | null
  /** Display label ("Table"/"Matrix"), not a canonical key — the backend resolves it. */
  selectedTable?: string | null
  expandedColumns?: string[]
  // ── chart config ──
  defaultChart?: string | null
  chartDimensions?: string[]
  chartMetrics?: string[]
  chartSortMetric?: string | null
  fieldValues?: Record<string, unknown>
  // ── kpi ──
  selectedKpi?: number
  // ── filters (module 08 filter model) ──
  defaultFilters?: unknown[]
  // ── per-widget date range ──
  selectedDate?: string | null
  compareType?: string | null
  compareEnabled?: boolean | null
  customDate?: WidgetDateRange
  customCompareDate?: WidgetDateRange
  // ── legacy passthrough ──
  cardButton?: boolean
  isCustomDashboard?: boolean
  /** Keys this model does not name (production carries 17+, e.g. `defaultVisible`). */
  [key: string]: unknown
}

/**
 * A parsed widget: the verbatim wire bag plus the derived kind.
 *
 * `kind` is a read-side view for the builder; `wire` is the only thing that is ever persisted.
 * Keeping them separate is what makes `serializeLayout ∘ parseLayout` identity by construction.
 */
export interface DashboardWidget {
  /** `null` when `visibleType` is absent or names a kind this client does not know. */
  readonly kind: WidgetKind | null
  readonly wire: DashboardWidgetWire
}
